import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { AddressValidationSchema } from '../shared/addressValidationSchema';
import { PaginatedListSchema } from '../shared/paginatedList';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { HistoricalTaxRateSnapshotValidationSchema } from '../tax-rate/create';
import {
  InvoiceStatusValidationEnum,
  InvoiceTaxSourceValidationEnum,
  InvoiceLineItemTypeValidationEnum,
  InvoiceChargeTypeValidationEnum,
} from './enum';

const InvoiceTaxRateDetailsSchema = z.object({
  country: z.string().nullish().meta({
    description: 'ISO 3166-1 alpha-2 country code the tax rate applies to.',
  }),
  percentageDecimal: z.number().nullish().meta({
    description: 'Tax percentage as a decimal (e.g. 0.2 for 20%).',
  }),
  state: z.string().nullish().meta({
    description: 'Subdivision the tax rate applies to (e.g. US state code).',
  }),
  taxType: z.string().nullish().meta({
    description: 'Tax classification (e.g. "vat", "sales_tax").',
  }),
});

export const InvoiceTaxBreakdownValidationSchema = z.object({
  taxType: z.string().nullish().meta({
    description: 'Tax classification for this line of the breakdown (e.g. "vat", "sales_tax").',
  }),
  taxabilityReason: z.string().nullish().meta({
    description:
      'Why the line was treated as taxable or not (e.g. "standard_rated", "zero_rated", "not_collecting").',
  }),
  taxableAmount: z.number().nullish().meta({
    description: 'Amount subject to tax for this line, in the smallest currency unit.',
  }),
  taxAmount: z.number().nullish().meta({
    description: 'Tax collected for this line, in the smallest currency unit.',
  }),
  taxRateDetails: InvoiceTaxRateDetailsSchema.nullish().meta({
    description: 'Detail of the tax rate applied to this line (country/state/type/percentage).',
  }),
});

export const InvoiceLineItemValidationSchema = z.object({
  type: InvoiceLineItemTypeValidationEnum.nullish().meta({
    description:
      'Line item type. "product": a product or variant. "setupFee": one-time setup fee on a subscription. "variantOption": line for a chosen variant option. "ticket": event ticket. "ticketFee": the per-ticket fee of the ticket line above it.',
  }),
  name: z.string().nullish().meta({
    description: 'Display name for the line item (typically the product title or variant label).',
  }),
  quantity: z.number().nullish().meta({
    description: 'Quantity purchased for this line.',
  }),
  unitPrice: z.number().nullish().meta({
    description: 'Per-unit price in the smallest currency unit, before discount.',
  }),
  amount: z.number().nullish().meta({
    description:
      'Line total (quantity × unitPrice) in the smallest currency unit, before discount.',
  }),
  bulkDiscountAmount: z.number().nullish().meta({
    description: 'Bulk-discount amount applied to this line, in the smallest currency unit.',
  }),
  couponDiscountAmount: z.number().nullish().meta({
    description: 'Coupon-discount amount applied to this line, in the smallest currency unit.',
  }),
  couponCode: z.string().nullish().meta({
    description: 'Code of the coupon that produced `couponDiscountAmount`, if any.',
  }),
});

export const InvoiceBulkDiscountValidationSchema = z.object({
  minQuantity: z.number().nullish().meta({
    description: 'Minimum quantity at which this discount tier activates.',
  }),
  maxQuantity: z.number().nullish().meta({
    description: 'Maximum quantity covered by this discount tier (omit for open-ended).',
  }),
  percentOff: z.number().nullish().meta({
    description: 'Percentage off per unit (0-100). Mutually exclusive with `amountOff`.',
  }),
  amountOff: z.number().nullish().meta({
    description:
      'Fixed amount off per unit, in the smallest currency unit. Mutually exclusive with `percentOff`.',
  }),
  amount: z.number().nullish().meta({
    description: 'Resolved total discount applied to the invoice, in the smallest currency unit.',
  }),
});

export const InvoiceFeeValidationSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Display name for the fee (e.g. "Shipping", "Processing").',
  }),
  amount: z.number().nullish().meta({
    description:
      'Fee amount in the smallest currency unit. For a fee with both a percentage and a fixed amount, the sum of `percentageAmount` and `fixedAmount`.',
  }),
  percentage: z.number().nullish().meta({
    description: 'Fee as a percentage of the subtotal (0-100), when defined that way.',
  }),
  percentageAmount: z.number().nullish().meta({
    description:
      'The part of `amount` from the percentage, in the smallest currency unit. Only set on a fee with both a percentage and a fixed amount. Can be 0, e.g. when a 100% coupon leaves nothing for the percentage to apply to.',
  }),
  fixedAmount: z.number().nullish().meta({
    description:
      'The fixed part of `amount`, in the smallest currency unit, already multiplied by quantity or tickets when the fee multiplies. Only set on a fee with both a percentage and a fixed amount.',
  }),
});

export const InvoiceCouponValidationSchema = z.object({
  id: ObjectIdStringValidationSchema.nullish().meta({
    description: 'Coupon record id. Resolve full coupon details by id if needed.',
  }),
  label: z.string().nullish().meta({
    description: 'Customer-facing label for the coupon.',
  }),
  code: z.string().nullish().meta({
    description: 'Coupon code redeemed on the invoice (case-sensitive as stored).',
  }),
  amountOff: z.number().nullish().meta({
    description:
      'Fixed amount off the invoice, in the smallest currency unit. Mutually exclusive with `percentOff`.',
  }),
  percentOff: z.number().nullish().meta({
    description: 'Percentage off the invoice (0-100). Mutually exclusive with `amountOff`.',
  }),
  discount: z.number().nullish().meta({
    description: 'Resolved discount applied to the invoice, in the smallest currency unit.',
  }),
});

export const InvoiceBillingValidationSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Buyer name captured at checkout.',
  }),
  email: z.string().nullish().meta({
    description: 'Buyer email captured at checkout. Lowercased.',
  }),
  phone: z.string().nullish().meta({
    description: 'Buyer phone captured at checkout, free-form.',
  }),
  address: AddressValidationSchema.nullish().meta({
    description: 'Buyer billing address captured at checkout.',
  }),
  taxId: z.string().nullish().meta({
    description: 'Buyer tax ID (e.g. VAT/GST number) captured at checkout.',
  }),
  taxIdType: z.string().nullish().meta({
    description: 'Type/jurisdiction of the buyer tax ID (e.g. "eu_vat", "us_ein").',
  }),
});

export const InvoiceSellerBillingValidationSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Seller legal/display name on the invoice.',
  }),
  email: z.string().nullish().meta({
    description: 'Seller support/billing email on the invoice.',
  }),
  taxId: z.string().nullish().meta({
    description: 'Seller tax registration ID printed on the invoice.',
  }),
  address: AddressValidationSchema.nullish().meta({
    description: 'Seller registration address printed on the invoice.',
  }),
});

export const InvoiceResponseValidationSchema = z
  .object({
    id: ObjectIdStringValidationSchema.meta({
      description: 'Unique identifier for the invoice.',
    }),
    sellerId: ObjectIdStringValidationSchema.meta({
      description: 'Seller account that owns this invoice.',
    }),
    customerId: ObjectIdStringValidationSchema.nullish().meta({
      description:
        'Customer record this invoice belongs to. For full customer profile fields (company, phone, tax ID), call get_customer with this id.',
    }),
    chargeId: ObjectIdStringValidationSchema.nullish().meta({
      description:
        'Originating payment (Charge) that generated this invoice. For refund details (amountRefunded, lastRefundAt, lastRefundReason) look up the Charge via list_payments — the invoice itself does not carry refund metadata.',
    }),
    chargeType: InvoiceChargeTypeValidationEnum.nullish().meta({
      description:
        'Whether `chargeId` is a payment (from a checkout page) or a booking (from an event page). Absent when the invoice has no charge.',
    }),
    livemode: z.boolean().meta({
      description:
        'True for live-mode invoices; false for test-mode invoices. Test- and live-mode data are isolated.',
    }),
    currency: z.string().meta({
      description: 'ISO 4217 currency code (lowercase, e.g. "usd", "gbp", "eur").',
    }),
    amount: z.number().meta({
      description:
        'Total invoice amount in the smallest currency unit (e.g. 10661 USD = $106.61, 500 JPY = ¥500). Divide by 100 before displaying, except for zero-decimal currencies like JPY.',
    }),
    amountRefunded: z.number().nullish().meta({
      description:
        'Amount refunded against this invoice in the smallest currency unit. Refund metadata lives on the originating Charge — query list_payments by chargeId for full refund history.',
    }),
    amountUsd: z.number().nullish().meta({
      description:
        'Total amount converted to USD at the time of the invoice, in the smallest currency unit (cents).',
    }),
    amountPaid: z.number().nullish().meta({
      description:
        'Amount paid in the smallest currency unit. Equal to `amount` when status is "paid".',
    }),
    amountDue: z.number().nullish().meta({
      description:
        'Outstanding amount in the smallest currency unit. Non-zero only on "open" or "uncollectible" invoices.',
    }),
    amountExcludingTax: z.number().nullish().meta({
      description: 'Subtotal of the invoice excluding tax, in the smallest currency unit.',
    }),
    taxAmount: z.number().nullish().meta({
      description: 'Total tax amount on the invoice in the smallest currency unit.',
    }),
    taxBreakdown: z.array(InvoiceTaxBreakdownValidationSchema).nullish().meta({
      description:
        'Per-line-item tax breakdown. Use this when a customer asks how tax was calculated.',
    }),
    taxSource: InvoiceTaxSourceValidationEnum.nullish().meta({
      description:
        'How tax was calculated: "stripe_tax" (Stripe Tax service) or "fixed_tax_rate" (merchant-defined rates). Null when the invoice has no tax.',
    }),
    taxRates: z.array(HistoricalTaxRateSnapshotValidationSchema).nullish().meta({
      description:
        'Historical snapshot of the merchant tax rates that applied at the time of the invoice. Preserved even if the merchant later edits or deletes the rate.',
    }),
    status: InvoiceStatusValidationEnum.meta({
      description:
        'Invoice status. "paid" — payment was collected. "open" — outstanding. "uncollectible" — payment failed and was abandoned. "refunded" — fully refunded. "partially_refunded" — partial refund issued. "void" — cancelled. "draft" — not yet finalised.',
    }),
    lineItems: z.array(InvoiceLineItemValidationSchema).nullish().meta({
      description:
        'Itemised list of products, variants, setup fees, and tickets on the invoice. Use when a customer asks what they were charged for.',
    }),
    subtotal: z.number().nullish().meta({
      description: 'Sum of line items before discount and tax, in the smallest currency unit.',
    }),
    subtotalAfterDiscount: z.number().nullish().meta({
      description:
        'Subtotal after bulk, coupon and complimentary discounts, before tax, in the smallest currency unit.',
    }),
    bulkDiscount: InvoiceBulkDiscountValidationSchema.nullish().meta({
      description: 'Quantity-based discount applied to the invoice, when one was in effect.',
    }),
    complimentaryDiscountAmount: z.number().nullish().meta({
      description:
        "Discount that brought a complimentary booking's total to 0, in the smallest currency unit. Absent otherwise.",
    }),
    fees: z.array(InvoiceFeeValidationSchema).nullish().meta({
      description: 'Additional fees on the invoice (e.g. shipping, processing).',
    }),
    processingFeeContribution: z
      .object({
        name: z
          .string()
          .nullish()
          .meta({ description: 'Name shown on the invoice, frozen at payment.' }),
        amount: z
          .number()
          .nullish()
          .meta({ description: 'Contribution in the smallest currency unit.' }),
      })
      .nullish()
      .meta({
        description:
          'What the customer chose to add to cover the processing fee, added after tax and never taxed. Not one of `fees`.',
      }),
    coupon: InvoiceCouponValidationSchema.nullish().meta({
      description:
        'Coupon applied to the invoice. Use when a customer asks about a discount on their invoice — `code` and `label` identify it; `amountOff` or `percentOff` describe the discount.',
    }),
    poNumber: z.string().nullish().meta({
      description:
        'Customer-supplied purchase-order number. Filterable via the `poNumber` query param.',
    }),
    poNumberLabel: z.string().nullish().meta({
      description:
        'Merchant-defined label for the PO number field (e.g. "Purchase Order", "Reference").',
    }),
    invoiceUuid: z.string().nullish().meta({
      description: 'Stable UUID for the invoice, used in invoice URLs.',
    }),
    invoiceNumber: z.string().nullish().meta({
      description:
        'Human-readable invoice number (e.g. "2026-000010"). Use when a customer asks for their invoice number.',
    }),
    invoiceUrl: z.string().nullish().meta({
      description:
        'Presigned hosted URL for the invoice PDF, safe to share with the buyer. Expires 1 hour after the response is generated — re-list or re-fetch the invoice to obtain a fresh URL. Absent for invoices that have no generated PDF (e.g. drafts).',
    }),
    billing: InvoiceBillingValidationSchema.nullish().meta({
      description:
        'Buyer billing details captured at checkout (name, email, phone, address, tax ID). May be empty for digital products.',
    }),
    sellerBilling: InvoiceSellerBillingValidationSchema.nullish().meta({
      description:
        "Seller registration details on the invoice (name, email, tax ID, address). Use when a customer asks about the seller's tax registration.",
    }),
    customerName: z.string().nullish().meta({
      description:
        'Customer name on the invoice. Denormalised from the Charge (preferred) or Customer record. Filterable via the `customerName` or `search` query params.',
    }),
    customerEmail: z.string().nullish().meta({
      description:
        'Customer email on the invoice. Denormalised from the Charge (preferred) or Customer record. Filterable via the `customerEmail` or `search` query params.',
    }),
    productTitle: z.string().nullish().meta({
      description:
        'Title of the first product line item on the invoice. Denormalised for search and filtering.',
    }),
    createdAt: z.iso.datetime().meta({
      description: 'ISO-8601 UTC timestamp the invoice was created.',
    }),
    updatedAt: z.iso.datetime().meta({
      description: 'ISO-8601 UTC timestamp the invoice was last updated.',
    }),
  })
  .meta({
    description:
      'Invoice generated from a payment (Charge) on the Checkout Page account. Read-only.',
  });

export const GetInvoicesResponseValidationSchema = PaginatedListSchema(
  InvoiceResponseValidationSchema,
);

export const InvoiceToolDataValidationSchema = InvoiceResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField.optional(),
});

export const InvoiceDetailToolResponseValidationSchema = z.object({
  data: InvoiceToolDataValidationSchema,
});

export const InvoicesToolResponseValidationSchema = PaginatedListSchema(
  InvoiceToolDataValidationSchema,
);
